@let active = activeUsers();
@let currentDelta = delta();
<p-dialog
  styleClass="tf-update-seats-dialog"
  [modal]="true"
  [visible]="open()"
  [draggable]="false"
  [resizable]="false"
  [closable]="!isConfirming()"
  [header]="'SETTINGS_BILLING.PLAN.SEATS.DIALOG_TITLE' | translate"
  (visibleChange)="onDialogVisibleChange($event)">
  <p class="tf-update-seats-dialog__subtitle">
    {{ 'SETTINGS_BILLING.PLAN.SEATS.DIALOG_SUBTITLE' | translate }}
  </p>

  <section class="tf-update-seats-dialog__picker">
    <div class="tf-update-seats-dialog__picker-left">
      <label class="tf-update-seats-dialog__field-label" for="tf-seats-input">
        {{ 'SETTINGS_BILLING.PLAN.SEATS.QUANTITY_LABEL' | translate }}
      </label>
      <div class="tf-update-seats-dialog__stepper">
        <button
          type="button"
          class="tf-update-seats-dialog__stepper-btn"
          [disabled]="seatsDraft() <= seatsMin() || isConfirming()"
          [attr.aria-label]="'GENERAL.DECREASE' | translate"
          (click)="adjustSeats(-1)">
          <i class="pi pi-minus" aria-hidden="true"></i>
        </button>
        <input
          id="tf-seats-input"
          class="tf-update-seats-dialog__stepper-input"
          type="number"
          inputmode="numeric"
          [min]="seatsMin()"
          [max]="9999"
          [value]="seatsDraft()"
          [disabled]="isConfirming()"
          (input)="onSeatsInput($event)"
          (change)="onSeatsInputCommit()" />
        <button
          type="button"
          class="tf-update-seats-dialog__stepper-btn"
          [disabled]="isConfirming()"
          [attr.aria-label]="'GENERAL.INCREASE' | translate"
          (click)="adjustSeats(1)">
          <i class="pi pi-plus" aria-hidden="true"></i>
        </button>
      </div>
      <p class="tf-update-seats-dialog__hint">{{ minimumHint(active) }}</p>
    </div>
    <div class="tf-update-seats-dialog__picker-right">
      <span class="tf-update-seats-dialog__field-label">
        {{ 'SETTINGS_BILLING.PLAN.SEATS.CHANGE_LABEL' | translate }}
      </span>
      <span
        class="tf-update-seats-dialog__delta"
        [class.tf-update-seats-dialog__delta--add]="currentDelta > 0"
        [class.tf-update-seats-dialog__delta--remove]="currentDelta < 0"
        [class.tf-update-seats-dialog__delta--none]="currentDelta === 0">
        @if (currentDelta > 0) {
          <i class="pi pi-arrow-up" aria-hidden="true"></i>
        } @else if (currentDelta < 0) {
          <i class="pi pi-arrow-down" aria-hidden="true"></i>
        }
        {{ deltaLabel(currentDelta) }}
      </span>
    </div>
  </section>

  @if (isPreviewing()) {
    <p class="tf-update-seats-dialog__muted tf-update-seats-dialog__muted--loading">
      <i class="pi pi-spin pi-spinner tf-update-seats-dialog__spinner" aria-hidden="true"></i>
      <span>{{ 'SETTINGS_BILLING.PLAN.SEATS.PREVIEW_LOADING' | translate }}</span>
    </p>
  } @else if (previewFailed()) {
    <p class="tf-update-seats-dialog__muted">
      {{ 'SETTINGS_BILLING.PLAN.SEATS.PREVIEW_UNAVAILABLE' | translate }}
    </p>
  } @else if (preview(); as p) {
    @if (p.direction === directions.Decrease) {
      <div class="tf-update-seats-dialog__callout">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        <span>
          {{
            'SETTINGS_BILLING.PLAN.SEATS.DECREASE_CALLOUT'
              | translate: { date: formatDate(p.effectiveAt ?? p.periodEnd) }
          }}
        </span>
      </div>
    }

    @if (p.direction === directions.Increase) {
      @let pr = proration();
      <section class="tf-update-seats-dialog__charge-block">
        <header class="tf-update-seats-dialog__charge-head">
          <div>
            <div class="tf-update-seats-dialog__charge-title">
              {{ 'SETTINGS_BILLING.PLAN.SEATS.CHARGED_TODAY' | translate }}
            </div>
            @if (pr) {
              <div class="tf-update-seats-dialog__charge-subtitle">
                {{
                  'SETTINGS_BILLING.PLAN.SEATS.CHARGED_TODAY_SUBTITLE'
                    | translate
                      : {
                          daysRemaining: pr.daysRemaining,
                          totalDays: pr.totalDays,
                          periodEnd: formatDate(p.periodEnd),
                        }
                }}
              </div>
            }
          </div>
          <div class="tf-update-seats-dialog__total-pill">
            {{ formatAmount(p.immediateAmountMinor, p.currency) }}
          </div>
        </header>
        <table class="tf-update-seats-dialog__charge-table">
          <tbody>
            <tr>
              <td>
                @if (pricePerSeatMinor(); as price) {
                  <div>
                    {{
                      'SETTINGS_BILLING.PLAN.SEATS.LINE_SEAT_QUANTITY'
                        | translate
                          : {
                              count: currentDelta,
                              pricePerSeat: formatAmount(price, p.currency),
                            }
                    }}
                  </div>
                } @else {
                  <div>
                    {{
                      'SETTINGS_BILLING.PLAN.SEATS.DELTA_ADD_MANY'
                        | translate: { count: currentDelta }
                    }}
                  </div>
                }
                @if (pr) {
                  <div class="tf-update-seats-dialog__charge-meta">
                    {{
                      'SETTINGS_BILLING.PLAN.SEATS.LINE_PRORATION_META'
                        | translate
                          : {
                              proration: formatPercent(pr.proration),
                              daysRemaining: pr.daysRemaining,
                              totalDays: pr.totalDays,
                            }
                    }}
                  </div>
                }
              </td>
              <td class="tf-update-seats-dialog__charge-amount">
                {{ formatAmount(p.immediateSubtotalMinor, p.currency) }}
              </td>
            </tr>
            <tr class="tf-update-seats-dialog__charge-total-row">
              <td>{{ 'SETTINGS_BILLING.PLAN.SEATS.TOTAL_DUE_TODAY' | translate }}</td>
              <td class="tf-update-seats-dialog__charge-amount">
                {{ formatAmount(p.immediateAmountMinor, p.currency) }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section
        class="tf-update-seats-dialog__charge-block tf-update-seats-dialog__charge-block--muted">
        <header class="tf-update-seats-dialog__charge-head">
          <div>
            <div class="tf-update-seats-dialog__charge-title">
              {{
                'SETTINGS_BILLING.PLAN.SEATS.FROM_DATE_ONWARDS'
                  | translate: { date: formatDate(p.periodEnd) }
              }}
            </div>
            <div class="tf-update-seats-dialog__charge-subtitle">
              {{
                'SETTINGS_BILLING.PLAN.SEATS.FROM_DATE_ONWARDS_SUBTITLE'
                  | translate: { seats: p.targetQuantity }
              }}
            </div>
          </div>
          <div class="tf-update-seats-dialog__total-pill tf-update-seats-dialog__total-pill--muted">
            {{ formatAmount(p.recurringAmountMinor, p.currency) }}
            <span>{{ 'SETTINGS_BILLING.PLAN.SEATS.PER_MONTH_SUFFIX' | translate }}</span>
          </div>
        </header>
        <table class="tf-update-seats-dialog__charge-table">
          <tbody>
            <tr>
              <td>
                @if (pricePerSeatMinor(); as price) {
                  {{
                    'SETTINGS_BILLING.PLAN.SEATS.LINE_SEAT_QUANTITY'
                      | translate
                        : {
                            count: p.targetQuantity,
                            pricePerSeat: formatAmount(price, p.currency),
                          }
                  }}
                }
              </td>
              <td class="tf-update-seats-dialog__charge-amount">
                {{ formatAmount(p.recurringSubtotalMinor, p.currency) }}
              </td>
            </tr>
            <tr class="tf-update-seats-dialog__charge-total-row">
              <td>{{ 'SETTINGS_BILLING.PLAN.SEATS.TOTAL_PER_MONTH' | translate }}</td>
              <td class="tf-update-seats-dialog__charge-amount">
                {{ formatAmount(p.recurringAmountMinor, p.currency) }}
                <span class="tf-update-seats-dialog__charge-suffix">
                  {{ 'SETTINGS_BILLING.PLAN.SEATS.PER_MONTH_SUFFIX' | translate }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <p class="tf-update-seats-dialog__vat-note">
        {{ 'SETTINGS_BILLING.PRICE_EXCLUDES_VAT' | translate }}
      </p>
    }

    @if (p.direction !== directions.Same) {
      <label class="tf-update-seats-dialog__consent">
        <input
          type="checkbox"
          [checked]="consentAccepted()"
          [disabled]="isConfirming()"
          (change)="onConsentChange($event)" />
        @if (p.direction === directions.Increase) {
          <span>{{ 'SETTINGS_BILLING.PLAN.SEATS.INCREASE_CONSENT' | translate }}</span>
        } @else {
          <span>{{ 'SETTINGS_BILLING.PLAN.SEATS.CHANGE_CONSENT' | translate }}</span>
        }
      </label>
    }
  }

  <ng-template #footer>
    <p-button
      severity="secondary"
      [outlined]="true"
      [label]="'SETTINGS_BILLING.PLAN.SEATS.CANCEL_BUTTON' | translate"
      [disabled]="isConfirming()"
      (onClick)="onCancel()" />
    <p-button
      [label]="confirmButtonLabel()"
      [loading]="isConfirming()"
      [disabled]="!canConfirm()"
      (onClick)="onConfirm()" />
  </ng-template>
</p-dialog>
